import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Modal, Input, Button, Spin } from "antd";
import { QRCodeSVG } from "qrcode.react";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { RootState } from "../../app/store";
// REDUX
import {
  usePostMfaEnrollInitMutation,
  usePostMfaEnrollVerifyMutation,
} from "../../redux/mfa/mfaAPI";
// AUTH HELPER
import GETJwtToken, { storeOfflineSessionToken } from "../../redux/authentication/authentication";

const TOTP_LENGTH = 6;

interface Props {
  open: boolean;
  onClose: () => void;
  onEnrolled?: () => void;
  /**
   * The user already has an authenticator linked, so this is a re-enrollment
   * and the current code is required. Defaults to `userLogged.mfa_enrolled`;
   * pass it when the caller knows better (e.g. enrolled earlier this session).
   */
  alreadyEnrolled?: boolean;
}

/** The error code /mfa/enroll/init gives a missing or wrong current code. */
const MFA_INVALID_CODE = "invalid_code";

/** True when an RTK Query error is the invalid-code 401 from /mfa/enroll/init. */
function isInvalidCodeError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const { status, data } = err as { status?: unknown; data?: { code?: unknown } | null };
  return status === 401 && data?.code === MFA_INVALID_CODE;
}

/**
 * MFAEnrollmentModal walks the user through TOTP enrollment for the local
 * (non-Cognito) MFA verifier. Used by edge / offline deployments — Cognito
 * deployments enroll TOTP via Amplify's CustomAmplifyMFASetUp during sign-in.
 *
 * Flow:
 *   1. User opens the modal → POST /api/mfa/enroll/init returns
 *      otpauth_url + secret_b32. The server stores the encrypted secret
 *      but does NOT mark the user as enrolled yet.
 *   2. We render the otpauth_url as a QR code; user scans it with their
 *      authenticator app (Authy / Google Authenticator / 1Password).
 *   3. User types the first generated 6-digit code → POST /api/mfa/enroll/
 *      verify confirms the secret round-trips correctly. On success the
 *      backend flips MFAEnrolled=true and issues a step-up token (so the
 *      user can immediately complete whatever sensitive action they were
 *      trying to gate).
 *
 * Re-enrollment (the user is already enrolled) replaces the secret, so the
 * server requires proof of the CURRENT authenticator: step 0 asks for its
 * code and step 1 sends it as `{ totp_code }` (LIFT-1085). A missing or wrong
 * code is a 401 `invalid_code`, and the user stays on step 0 to retry. After
 * that the user MUST re-scan, or the next verification fails.
 */
function MFAEnrollmentModal({
  open,
  onClose,
  onEnrolled = undefined,
  alreadyEnrolled = undefined,
}: Props) {
  const [t] = useTranslation("global");
  const userLogged = useSelector((state: RootState) => state.home?.userLogged);
  const reenroll = alreadyEnrolled ?? userLogged?.mfa_enrolled === true;

  const [step, setStep] = useState<"current" | "loading" | "scan" | "verify" | "done">("loading");
  const [otpauthUrl, setOtpauthUrl] = useState("");
  const [secretB32, setSecretB32] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [currentCode, setCurrentCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [triggerEnrollInit] = usePostMfaEnrollInitMutation();
  const [triggerEnrollVerify, { isLoading: verifying }] = usePostMfaEnrollVerifyMutation();

  /**
   * POST /mfa/enroll/init. `currentTotpCode` is the current authenticator's
   * code, required when re-enrolling; a first enrollment sends `{}`.
   */
  const initiateEnrollment = async (currentTotpCode?: string) => {
    setStep("loading");
    setErrorMessage("");
    setTotpCode("");
    try {
      const authHeader = await GETJwtToken();
      const body = currentTotpCode ? { totp_code: currentTotpCode } : {};
      const result = await triggerEnrollInit({ token: authHeader, body }).unwrap();
      if (!result?.otpauth_url || !result?.secret_b32) {
        throw new Error("Server did not return enrollment payload");
      }
      setOtpauthUrl(result.otpauth_url);
      setSecretB32(result.secret_b32);
      setStep("scan");
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.warn("MFAEnrollmentModal: init failed", err?.status ?? err?.message);
      if (currentTotpCode !== undefined) {
        // Re-enrollment: stay on the current-code step so the user can retry.
        setCurrentCode("");
        setErrorMessage(
          isInvalidCodeError(err)
            ? t("authentication.mfaReenrollCodeInvalid")
            : "Could not start enrollment. Please retry.",
        );
        setStep("current");
        return;
      }
      setErrorMessage("Could not start enrollment. Please retry.");
      setStep("scan");
    }
  };

  const onSubmitCurrentCode = () => {
    if (currentCode.length !== TOTP_LENGTH) return;
    initiateEnrollment(currentCode);
  };

  const onSubmitVerify = async () => {
    setErrorMessage("");
    if (totpCode.length !== TOTP_LENGTH) {
      setErrorMessage(`Code must be ${TOTP_LENGTH} digits`);
      return;
    }
    try {
      const authHeader = await GETJwtToken();
      const result = await triggerEnrollVerify({
        token: authHeader,
        body: { totp_code: totpCode },
      }).unwrap();
      if (!result?.step_up_token) {
        throw new Error("Server did not return a step-up token on enrollment verify");
      }
      // Persist the step-up token so the immediate next sensitive call
      // satisfies RequireFreshMFA (typical UX: enroll → claim secret).
      storeOfflineSessionToken(result.step_up_token);
      setStep("done");
      if (onEnrolled) onEnrolled();
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.warn("MFAEnrollmentModal: verify failed", err?.status ?? err?.message);
      setErrorMessage("That code did not match. Wait for the next one and try again.");
    }
  };

  useEffect(() => {
    if (open) {
      if (reenroll) {
        setErrorMessage("");
        setCurrentCode("");
        setStep("current");
      } else {
        initiateEnrollment();
      }
    }
    // initiateEnrollment is stable for this modal's lifetime; intentionally
    // omitted from deps to avoid re-firing on every render.
  }, [open]);

  const renderBody = () => {
    if (step === "loading") {
      return (
        <div className="text-center" style={{ padding: "40px 0" }}>
          <Spin />
          <p className="mt-3">Preparing your authenticator secret…</p>
        </div>
      );
    }

    if (step === "current") {
      return (
        <>
          <p className="drawer__text" style={{ marginBottom: "12px" }}>
            {t("authentication.mfaReenrollCurrentCodePrompt")}
          </p>
          <Input
            data-testid="mfa-current-code"
            placeholder={t("authentication.mfaCurrentCodePlaceholder")}
            onChange={(e) => setCurrentCode(e.target.value)}
            onPressEnter={onSubmitCurrentCode}
            value={currentCode}
            maxLength={TOTP_LENGTH}
            status="warning"
          />
          {errorMessage && (
            <p className="generalStyles__error" style={{ marginTop: "5px", marginBottom: "0px" }}>
              {errorMessage}
            </p>
          )}
        </>
      );
    }

    if (step === "done") {
      return (
        <div className="text-center" style={{ padding: "20px 0" }}>
          <p className="mt-3 drawer__text">
            <b className="generalStyles__success">MFA enrolled.</b> Your authenticator app is now
            linked. Future sensitive actions will prompt for a 6-digit code.
          </p>
        </div>
      );
    }

    return (
      <>
        <div className="text-center" style={{ marginBottom: "16px" }}>
          <p className="drawer__text" style={{ marginBottom: "12px" }}>
            Scan this QR code with your authenticator app (Authy / Google Authenticator /
            1Password), then enter the first 6-digit code it generates.
          </p>
          {otpauthUrl && (
            <div style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}>
              <QRCodeSVG value={otpauthUrl} size={192} includeMargin />
            </div>
          )}
          {secretB32 && (
            <p
              className="drawer__text"
              style={{ fontFamily: "monospace", fontSize: "12px", wordBreak: "break-all" }}
            >
              Or paste manually: <b>{secretB32}</b>
            </p>
          )}
        </div>
        <Input
          placeholder="6-digit code"
          onChange={(e) => setTotpCode(e.target.value)}
          value={totpCode}
          maxLength={TOTP_LENGTH}
          status="warning"
        />
        {errorMessage && (
          <p className="generalStyles__error" style={{ marginTop: "5px", marginBottom: "0px" }}>
            {errorMessage}
          </p>
        )}
      </>
    );
  };

  const renderFooter = () => {
    if (step === "done") {
      return (
        <div className="generalStyles__flexEnd">
          <Button onClick={onClose} className="buttonStyle__1">
            {t("general.close")}
          </Button>
        </div>
      );
    }

    if (step === "current") {
      return (
        <div className="generalStyles__flexEnd">
          <Button
            onClick={onSubmitCurrentCode}
            className="buttonStyle__1"
            disabled={currentCode.length !== TOTP_LENGTH}
            style={{ marginRight: "8px" }}
          >
            {t("authentication.mfaReenrollContinue")}
          </Button>
          <Button onClick={onClose} className="buttonStyle__4">
            {t("general.close")}
          </Button>
        </div>
      );
    }

    if (step === "scan") {
      return (
        <div className="generalStyles__flexEnd">
          <Button
            onClick={onSubmitVerify}
            className="buttonStyle__1"
            disabled={verifying || totpCode.length !== TOTP_LENGTH}
            loading={verifying}
            style={{ marginRight: "8px" }}
          >
            Verify &amp; enable
          </Button>
          <Button onClick={onClose} className="buttonStyle__4">
            {t("general.close")}
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Enable Multi-Factor Authentication"
      footer={renderFooter()}
      destroyOnClose
    >
      {renderBody()}
    </Modal>
  );
}

export default MFAEnrollmentModal;
